import type {
  ChallengeMode,
  DailyChallenge,
  FoundArticle,
  ScoreEntry,
} from "./types";

/** One player's outcome, as shown in stats and share text. */
export interface RunResult {
  seconds: number;
  clicks: number;
  found: number;
  total: number;
}

export function challengeMode(challenge: DailyChallenge): ChallengeMode {
  return challenge.mode ?? { kind: "daily" };
}

/**
 * The public page for a challenge. Daily and replay runs share the archive
 * page of that day: before midnight it shows only the route, afterwards the
 * results, so a shared link never spoils and never goes stale.
 */
export function challengePagePath(challenge: DailyChallenge): string {
  const mode = challengeMode(challenge);
  if (mode.kind === "custom") return `/c/${mode.public_id}`;
  return `/archive/${challenge.language}/${challenge.date}`;
}

export function scoreboardUrl(challenge: DailyChallenge): string {
  const mode = challengeMode(challenge);
  const language = encodeURIComponent(challenge.language);
  switch (mode.kind) {
    case "custom":
      return `/api/custom/${encodeURIComponent(mode.public_id)}/scoreboard`;
    case "replay":
      return `/api/daily/archive/scoreboard?language=${language}&date=${encodeURIComponent(challenge.date)}`;
    case "daily":
      return `/api/daily/scoreboard?language=${language}`;
  }
}

/** Clicks of a run: every visited article except the start it began on. */
export function clickCount(visited: number): number {
  return Math.max(0, visited - 1);
}

export function resultFromGame(
  foundArticles: FoundArticle[],
  total: number,
  seconds: number,
): RunResult {
  return {
    seconds,
    clicks: clickCount(foundArticles.length),
    found: foundArticles.filter((fa) => fa.is_target).length,
    total,
  };
}

export function resultFromScore(score: ScoreEntry): RunResult {
  return {
    seconds: score.completion_time_seconds,
    clicks: clickCount(score.moves.length),
    found: score.found_count,
    total: score.target_count,
  };
}

/** Leaderboard order, as in the other games' "Fewest Moves / Fastest Time". */
export type ScoreSort = "time" | "moves";

/**
 * Best first. Finding more targets always ranks higher; among equals the
 * chosen measure decides and the other one breaks ties.
 */
export function sortScores(scores: ScoreEntry[], by: ScoreSort): ScoreEntry[] {
  const clicks = (score: ScoreEntry) => clickCount(score.moves.length);
  const time = (score: ScoreEntry) => score.completion_time_seconds;
  const [first, second] = by === "moves" ? [clicks, time] : [time, clicks];
  return [...scores].sort(
    (a, b) =>
      b.found_count - a.found_count ||
      first(a) - first(b) ||
      second(a) - second(b),
  );
}

export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const pad = (n: number) => n.toString().padStart(2, "0");
  if (s >= 3600) {
    return `${Math.floor(s / 3600)}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
  }
  return `${Math.floor(s / 60)}:${pad(s % 60)}`;
}

/** Wordle-style target row: one square per target, no route spoilers. */
export function resultSquares(result: RunResult): string {
  return "🟩".repeat(result.found) + "⬜".repeat(result.total - result.found);
}

/**
 * Link to the challenge page carrying the sharer's result, which the page
 * and its preview image turn into "Can you beat 2:31?". With the sharer's
 * player id, the page can also show their route behind a spoiler.
 */
export function shareUrl(
  origin: string,
  challenge: DailyChallenge,
  result: RunResult,
  playerId?: string | null,
): string {
  const params = new URLSearchParams({
    t: String(Math.round(result.seconds)),
    n: String(result.clicks),
    f: String(result.found),
  });
  // The start of the id is enough to find the run among that challenge's.
  const runRef = playerId?.replace(/-/g, "").slice(0, 8);
  if (runRef) params.set("r", runRef);
  return `${origin}${challengePagePath(challenge)}?${params}`;
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest;

  const challenge = (mode?: ChallengeMode): DailyChallenge => ({
    id: "c1",
    mode,
    challenge_date: "2026-10-14",
    date: "2026-10-14",
    language: "en",
    start_article: { id: 1, title: "Start", description: null },
    target_articles: [],
  });

  describe("share", () => {
    it("links dailies and replays to the archive page of their day", () => {
      expect(challengePagePath(challenge())).toBe("/archive/en/2026-10-14");
      expect(challengePagePath(challenge({ kind: "replay" }))).toBe(
        "/archive/en/2026-10-14",
      );
      expect(
        challengePagePath(challenge({ kind: "custom", public_id: "ab23cdx9" })),
      ).toBe("/c/ab23cdx9");
    });

    it("picks the scoreboard of the run's leaderboard", () => {
      expect(scoreboardUrl(challenge())).toBe(
        "/api/daily/scoreboard?language=en",
      );
      expect(scoreboardUrl(challenge({ kind: "replay" }))).toBe(
        "/api/daily/archive/scoreboard?language=en&date=2026-10-14",
      );
      expect(
        scoreboardUrl(challenge({ kind: "custom", public_id: "ab23cdx9" })),
      ).toBe("/api/custom/ab23cdx9/scoreboard");
    });

    it("puts the result into the shared link", () => {
      const result = { seconds: 151, clicks: 14, found: 2, total: 3 };
      expect(shareUrl("https://wikiparty.org", challenge(), result)).toBe(
        "https://wikiparty.org/archive/en/2026-10-14?t=151&n=14&f=2",
      );
      expect(
        shareUrl(
          "https://wikiparty.org",
          challenge(),
          result,
          "1a2b3c4d-5e6f-4000-8000-000000000001",
        ),
      ).toBe(
        "https://wikiparty.org/archive/en/2026-10-14?t=151&n=14&f=2&r=1a2b3c4d",
      );
      expect(resultSquares(result)).toBe("🟩🟩⬜");
    });

    it("does not count the start article as a click", () => {
      expect(clickCount(0)).toBe(0);
      expect(clickCount(1)).toBe(0);
      expect(clickCount(15)).toBe(14);
    });

    it("sorts by fewest moves or fastest time, targets first", () => {
      const score = (
        name: string,
        found: number,
        seconds: number,
        visited: number,
      ): ScoreEntry => ({
        player_name: name,
        completion_time_seconds: seconds,
        found_count: found,
        target_count: 3,
        moves: Array.from({ length: visited }, (_, i) => ({
          title: `A${i}`,
          duration_seconds: i,
          is_target: false,
        })),
      });
      const scores = [
        score("slow-direct", 3, 300, 5),
        score("fast-wandering", 3, 100, 20),
        score("partial", 2, 10, 2),
        score("fast-direct", 3, 100, 8),
      ];
      const names = (by: ScoreSort) =>
        sortScores(scores, by).map((s) => s.player_name);

      expect(names("time")).toEqual([
        "fast-direct",
        "fast-wandering",
        "slow-direct",
        "partial",
      ]);
      expect(names("moves")).toEqual([
        "slow-direct",
        "fast-direct",
        "fast-wandering",
        "partial",
      ]);
    });

    it("formats times like the archive pages", () => {
      expect(formatClock(0)).toBe("0:00");
      expect(formatClock(151)).toBe("2:31");
      expect(formatClock(3725)).toBe("1:02:05");
    });
  });
}
